import React from "react";
import { Typography, Link, Box, List, ListItem, ListItemText, useTheme, Grid } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import regFormImage from '../../assets/madrasati-reg-form.png';
import madrasatiLightLogo from '../../assets/madrasati-light.svg';
import madrasatiDarkLogo from '../../assets/madrasati-dark.svg';
import SectionDivider from '../ui/SectionDivider';

const SmsOptInPolicy = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        overflow: 'hidden',
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(52, 152, 219, 0.09) 0%, rgba(41, 128, 185, 0.15) 100%)`,
          top: '-100px',
          right: '-100px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(41, 128, 185, 0.12) 0%, rgba(52, 152, 219, 0.18) 100%)`,
          bottom: '50px',
          left: '50px',
        }}
      />

      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          padding: { xs: 3, md: 5 },
          zIndex: 1,
        }}
      >
        {/* Logo at top */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRadius: '24px',
              border: '2px solid transparent',
              background: 'transparent',
              transition: 'all 0.3s ease-in-out',
              padding: { xs: 1.5, md: 2 },
              display: 'inline-block',
              '&:hover': {
                border: '2px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'dark' ? madrasatiDarkLogo : madrasatiLightLogo}
              alt="Madrasati Logo"
              onClick={() => navigate('/')}
              sx={{
                width: '100%',
                maxWidth: '200px',
                height: 'auto',
                objectFit: 'contain',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                display: 'block',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                },
              }}
            />
          </Box>
        </Box>

        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.secondary.contrastText,
            mb: 3,
            textAlign: 'center',
          }}
        >
          SMS Consent & Opt-In Policy
        </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ color: theme.palette.secondary.contrastText }}
        >
          Express Written Consent
        </Typography>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              Madrasati collects express written consent from users through our web-based registration form. When you provide your phone number and click the "Register" button, you are providing express written consent to receive text messages from us.
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              This consent is collected and documented with the following information:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="• Timestamp of consent"
                  secondary="We record when you submit the registration form"
                  primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
                  secondaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText, opacity: 0.8 } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Source of consent"
                  secondary="Web registration form at my-madrasah.com/register"
                  primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
                  secondaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText, opacity: 0.8 } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Clear disclosure"
                  secondary="The registration form clearly states the types of messages you will receive"
                  primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
                  secondaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText, opacity: 0.8 } }}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Box
                component="img"
                src={regFormImage}
                alt="Registration form showing SMS consent"
                sx={{
                  width: '100%',
                  maxWidth: 350,
                  height: 'auto',
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SectionDivider/>
      
      {/* Centered content sections */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: theme.palette.secondary.contrastText, textAlign: 'center' }}
          >
            Message Content & Frequency
          </Typography>
          <Typography 
            variant="body1" 
            paragraph
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            By providing consent, you will receive text messages related to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="• Attendance updates and notifications"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Payment reminders and confirmations"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Important service-related announcements"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Account security notifications"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
          </List>
          <Typography 
            variant="body1"
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            Message frequency varies based on your account activity and preferences. The number of messages you receive will depend on your account settings and activity level.
          </Typography>
        </Box>

        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: theme.palette.secondary.contrastText, textAlign: 'center' }}
          >
            Opt-Out Instructions
          </Typography>
          <Typography 
            variant="body1" 
            paragraph
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            You can opt out of receiving text messages at any time by:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="• Replying STOP to any message"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Updating your preferences in your account settings"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Contacting our support team"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
          </List>
          <Typography 
            variant="body1"
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            After opting out, you will receive one final message confirming your opt-out.
          </Typography>
        </Box>

        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: theme.palette.secondary.contrastText, textAlign: 'center' }}
          >
            Support & Help
          </Typography>
          <Typography 
            variant="body1" 
            paragraph
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            For assistance with text messages:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="• Reply HELP to any message"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Email us at techtalentedllc@gmail.com"
                primaryTypographyProps={{ sx: { color: theme.palette.secondary.contrastText } }}
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: theme.palette.secondary.contrastText, textAlign: 'center' }}
          >
            Carrier Rates & Charges
          </Typography>
          <Typography 
            variant="body1"
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            Message and data rates may apply. Please consult your mobile service carrier plan for details. Madrasati is not responsible for any charges incurred from your mobile carrier.
          </Typography>
        </Box>

        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: theme.palette.secondary.contrastText, textAlign: 'center' }}
          >
            Privacy & Data Protection
          </Typography>
          <Typography 
            variant="body1" 
            paragraph
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            Your phone number and personal information are protected under our Privacy Policy. We will never sell or share your contact information with third parties for marketing purposes.
          </Typography>
          <Typography 
            variant="body1"
            sx={{ color: theme.palette.secondary.contrastText }}
          >
            For more information, please review our{" "}
            <Link component={RouterLink} to="/privacy-policy" color="primary">
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default SmsOptInPolicy;
